import { Input } from '@workspace/ui/components/input';
import { forwardRef, type InputHTMLAttributes, useEffect, useState } from 'react';
import TimerWorker from './worker?worker';
import { Icon } from '../icon/icon';
import { secondToStringTime } from './secondToStringTime';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  search?: boolean;
  helperText?: string;
  errorText?: string;
  count?: {
    show: boolean;
    maxLength: number;
  };
  timer?: {
    show: boolean;
    limit: number;
  };
  showClearButton?: boolean;
  onTimerEnd?: () => unknown;
  onChange?: (event: any) => unknown;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      helperText,
      errorText,
      count,
      timer,
      onTimerEnd,
      onChange,
      search,
      showClearButton = true,
      ...props
    },
    ref,
  ) => {
    const hasError = Boolean(errorText);

    const [text, setText] = useState('');
    const [time, setTime] = useState(timer?.limit);

    if (timer && timer.limit > 3600) {
      throw new Error('타이머 시간은 3600초를 초과할 수 없습니다.');
    }

    useEffect(() => {
      const worker = new TimerWorker();

      worker.postMessage({ time: timer?.limit });
      worker.onmessage = (e) => {
        setTime(e.data.leftTime);
        if (e.data.leftTime < 0) {
          setTime(0);
          onTimerEnd?.();
          worker.terminate();
        }
      };

      return () => {
        worker.terminate();
      };
    }, [timer?.limit, onTimerEnd]);

    return (
      <div>
        <div
          className='relative flex gap-2 w-full h-10 px-4 rounded-[8px] bg-[#F5F5F5] overflow-hidden focus-within:outline focus-within:outline-1 focus-within:outline-[#101010]'
          style={
            hasError
              ? {
                  outline: '1px solid #D15445',
                  outlineColor: '#D15445',
                  backgroundColor: '#FCEDEB',
                }
              : undefined
          }
        >
          {search && (
            <Icon
              name='Search'
              size={24}
              color='#101010'
              wrapperStyle={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            />
          )}
          <Input
            ref={ref}
            className='h-full p-0 bg-transparent border-none rounded-l-[0.5rem] text-[#101010] text-sm font-normal tracking-[-0.1px] shadow-none placeholder:text-[#BFBFBF] caret-[#3C78F4] focus-visible:outline-0 focus-visible:outline-offset-0 focus-visible:outline-none focus-visible:ring-offset-0 focus-visible:ring-0'
            value={props.value || text}
            {...props}
            onChange={(e) => {
              let nextText = e.target.value;
              if (count && e.target.value.length > count.maxLength) {
                nextText = e.target.value.slice(0, count.maxLength);
                e.target.value = nextText;
              }
              onChange?.(e.target.value);
              setText(e.target.value);
            }}
          />
          {((showClearButton && text) || timer) && (
            <div className='flex items-center gap-4'>
              {showClearButton && text.length > 0 && (
                <Icon
                  name='CircleXFilled'
                  size={16}
                  color='#bfbfbf'
                  onClick={() => {
                    setText('');
                    onChange?.('');
                  }}
                  wrapperStyle={{ cursor: 'pointer' }}
                />
              )}
              {timer?.show && (
                <div
                  className='w-10 text-right text-[#3C78F4] text-sm font-normal tracking-[-0.1px]'
                  style={{ color: hasError ? '#D15445' : '#3c78f4' }}
                >
                  {secondToStringTime(time || 0)}
                </div>
              )}
            </div>
          )}
        </div>
        {(helperText || errorText || count?.show) && (
          <div
            className='relative mt-2 flex justify-between items-start gap-2 box-border text-xs font-normal'
            style={{ color: hasError ? '#D15445' : '#595959' }}
          >
            <div className='flex-grow flex items-center gap-1'>
              {errorText && <Icon name='ExclamationMarkFilled' size={16} color='#d15445' />}
              {errorText || helperText}
            </div>
            {count?.show && (
              <div className='flex-shrink-0'>
                {text.length} / {count.maxLength}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
TextField.displayName = 'TextField';

export { TextField };
